import React, { Suspense, useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getInt } from 'utils/functions';
import { Grid } from '@mui/material';
import Portal from '../../../../utils/portal/Portal';
import { withAreaGetter } from '../../../../providers/AreasConsumers';
import { areas } from '../../../../constants/areas';
import FacilityHeader from './FacilityHeader';
import { usePrevious } from 'customHooks/usePrevious';
import FacilityStepper from './FacilityStepper';
import FacilitySwitcher from './FacilitySwitcher';
import initialSteps from '../initialSteps';
import FacilityGeneralDataContainer from '../../edit/generalData/components/FacilityGeneralDataContainer';
import FacilitySublimitsContainer from '../../edit/sublimits/components/FacilitySublimitsContainer';
import FacilityReportsContainer from '../../edit/reports/FacilityReportsContainer';
import FacilityKpiIndicatorsStepContainer from '../../edit/kpiIndicators/components/FacilityKpiIndicatorsStepContainer';
import { loadFacilityReports } from '../actionCreators';
import { loadDocumentRights } from 'actions/actionCreators/documentRightsActions';
import { RightsContext } from '../../../../providers/RightsContext';
import { getDocumentRightsByStateKey } from 'constants/common';
import { useSubscription } from '@apollo/client';
import { DOCUMENT_UPDATED } from 'features/document/edit/subscriptions';
import { showInfo } from 'actions/actionCreators/snackbarActions';
import { selectUserId } from 'selectors/userInfoSelectors';
import { setContent, showRightSideBar, resetContent } from 'components/layout/RightSideBar';
import DocumentComments from 'features/offer/edit/sidebar/components/comments/DocumentComments';
import { useTitle } from 'utils/hooks/useTitle';
import WrappedFakeText from 'components/common/WrappedFakeText';

const FacilityWizard = ({ documentId, siteId, facilityHeader, isNewFacility, history, getElemByAreaKey, locationPathName }) => {
  const { t } = useTranslation();
  const mounted = useRef(false);
  const rightsStateKey = getDocumentRightsByStateKey(facilityHeader.documentStateId, facilityHeader.documentTypeId);
  const rights = useSelector(state => state.documentRights[rightsStateKey] || {}, shallowEqual);
  const userId = useSelector(state => selectUserId(state));

  let firstUpdate = false;
  const dispatch = useDispatch();

  const [activeStep, setActiveStep] = useState(0);
  const [localLastSavedStep, setLocalLastSavedStep] = useState(0);
  const [showReportsModal, setShowReportsModal] = useState(false);
  const [steps, setSteps] = useState(initialSteps);
  const [openFacilityActions, setOpenFacilityActions] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const prevFacilityHeaderActiveStep = usePrevious(facilityHeader.activeStep);

  const handleOpenFacilityActions = event => {
    setAnchorEl(event.currentTarget);
    setOpenFacilityActions(true);
  };
  const handleCloseFacilityActions = () => setOpenFacilityActions(false);

  useEffect(() => {
    mounted.current = true;
    if (!isNewFacility) {
      if (!mounted.current) {
        return;
      }
      changeStep(getInt(facilityHeader.activeStep));
    } else {
      checkTransitions();
    }

    dispatch(loadDocumentRights(documentId, siteId, facilityHeader.documentStateId, facilityHeader.documentTypeId));
    firstUpdate = true; // eslint-disable-line react-hooks/exhaustive-deps
    showRightSideBar(true);

    return () => {
      mounted.current = false;
      resetContent();
      showRightSideBar(false);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSidebarContent();
    if (firstUpdate || !mounted.current || rights.isLoaded) return;

    dispatch(loadDocumentRights(documentId, siteId, facilityHeader.documentStateId, facilityHeader.documentTypeId));
  }, [rights]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (firstUpdate || !mounted.current) return;

    const step = steps.find(a => locationPathName.includes(a.route.replace('{id.documentId}', documentId).replace('{id.siteId}', siteId)));

    if (step) {
      setActiveStep(step.id);
      checkTransitions(getInt(locationPathName.activeStep));
    } else {
      changeStep(activeStep);
    }
  }, [locationPathName]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (firstUpdate || !mounted.current) return;

    if (facilityHeader.activeStep !== prevFacilityHeaderActiveStep) {
      checkTransitions(getInt(facilityHeader.activeStep));
    }
  }, [facilityHeader]); // eslint-disable-line react-hooks/exhaustive-deps

  const setSidebarContent = useCallback(() => {
    if (!rights || Object.keys(rights).length === 0) return;
    setContent(
      <RightsContext.Provider value={rights}>
        <DocumentComments documentId={documentId} siteId={siteId} history={history} fullScreen={false} />
      </RightsContext.Provider>
    );
  }, [documentId, siteId, rights, history]);

  const onChangeShowReportsModal = value => {
    setShowReportsModal(value);
  };

  const onChangeActiveStep = value => {
    setActiveStep(value);
  };

  const changeStep = stepId => {
    onChangeActiveStep(stepId);
    checkTransitions(stepId);
    const step = steps.find(a => a.id === stepId);
    if (step) {
      const defaultRoute = `/facilities/${documentId}/${siteId}`;
      if (
        defaultRoute.toLowerCase() === locationPathName.toLowerCase() ||
        defaultRoute.toLowerCase() + '/' === locationPathName.toLowerCase()
      ) {
        onStepClick(step, true);
      } else {
        const urlStep = steps.find(a =>
          locationPathName
            .toLowerCase()
            .includes(a.route.replace('{id.documentId}', documentId).replace('{id.siteId}', siteId).toLowerCase())
        );
        if (urlStep) {
          if (urlStep.disabled) {
            onStepClick(step);
          } else {
            onChangeActiveStep(urlStep.id);
            onStepClick(urlStep);
          }
        } else {
          onChangeActiveStep(-1);
        }
      }
    }
  };

  const onStepClick = (step, isRedirect = false) => {
    if (!documentId) return;

    const route = step.route.replace('{id.documentId}', documentId).replace('{id.siteId}', siteId);

    if (locationPathName === route) return;

    if (isRedirect) {
      history.replace(route);
    } else {
      history.push(route);
    }
  };

  const checkTransitions = lastSavedStep => {
    var localSteps = steps.slice();
    let tempSavedStep = lastSavedStep || localLastSavedStep;
    if (tempSavedStep < 0) tempSavedStep = localLastSavedStep;

    if (isNewFacility) {
      for (let i = 1; i < localSteps.length; i++) {
        localSteps[i].disabled = true;
      }
    } else {
      switch (tempSavedStep) {
        case 0:
          localSteps.forEach(item => (item.disabled = false));
          break;
        case 1:
          break;
        case 2:
          break;
        case 3:
          break;

        default:
          localSteps.forEach(item => (item.disabled = true));
          break;
      }
    }

    setSteps(localSteps);
    setLocalLastSavedStep(tempSavedStep);
  };

  const titleData = {
    documentNumber: facilityHeader.documentNumber,
    name: facilityHeader.partnerGroupId ? facilityHeader.partnerGroupName : facilityHeader.partnerId ? facilityHeader.partnerName : ''
  };

  const headerContent = !isNewFacility ? (
    <FacilityHeader
      documentId={documentId}
      siteId={siteId}
      data={titleData}
      history={history}
      onChangeActiveStep={onChangeActiveStep}
      openFacilityActions={openFacilityActions}
      handleCloseFacilityActions={handleCloseFacilityActions}
      handleOpenFacilityActions={handleOpenFacilityActions}
      loadFacilityReports={() => dispatch(loadFacilityReports(documentId, siteId))}
      onChangeShowReportsModal={onChangeShowReportsModal}
      anchorEl={anchorEl}
    />
  ) : (
    <React.Fragment>{t('Facility.NewFacilityPageTitle')}</React.Fragment>
  );

  useTitle(
    isNewFacility || !facilityHeader.documentNumber ? t('Facility.NewFacilityPageTitle') : t('Facility.PageTitle', { data: titleData })
  );

  const facilityGeneralDataContainer = route => (
    <FacilityGeneralDataContainer
      documentId={getInt(route.match.params.documentId)}
      siteId={getInt(route.match.params.siteId)}
      history={history}
    />
  );

  const facilitySublimitsContainer = route => (
    <FacilitySublimitsContainer documentId={getInt(route.match.params.documentId)} siteId={getInt(route.match.params.siteId)} />
  );

  const facilityKpiIndicatorsContainer = route => (
    <FacilityKpiIndicatorsStepContainer
      documentId={getInt(route.match.params.documentId)}
      siteId={getInt(route.match.params.siteId)}
      isFromFacility={true}
      hasFacilityOffer={facilityHeader.offerId !== undefined && facilityHeader.offerId !== null}
    />
  );

  const createFacilityContainer = () => <FacilityGeneralDataContainer history={history} />;

  useSubscription(DOCUMENT_UPDATED, {
    variables: { documentId, siteId, userId: userId },
    onSubscriptionData: data => {
      if (data) dispatch(showInfo(t('Facility.ModifiedData'), false, true));
    }
  });

  return (
    <div>
      <RightsContext.Provider value={rights}>
        <Grid container spacing={2} px={2}>
          <Grid item xs={12}>
            <FacilityStepper steps={steps} onStepClick={onStepClick} activeStep={activeStep} />
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <Suspense fallback={<WrappedFakeText onPaper />}>
              <FacilitySwitcher
                facilityGeneralDataContainer={facilityGeneralDataContainer}
                facilitySublimitsContainer={facilitySublimitsContainer}
                facilityKpiIndicatorsContainer={facilityKpiIndicatorsContainer}
                createFacilityContainer={createFacilityContainer}
                history={history}
              />
            </Suspense>
          </Grid>
        </Grid>
        <Portal container={getElemByAreaKey(areas.header)}>{headerContent}</Portal>
      </RightsContext.Provider>

      {showReportsModal && (
        <FacilityReportsContainer documentId={documentId} siteId={siteId} onHandleShowReportsModal={onChangeShowReportsModal} />
      )}
    </div>
  );
};

FacilityWizard.propTypes = {
  facilityHeader: PropTypes.object,
  documentId: PropTypes.number,
  siteId: PropTypes.number,
  isNewFacility: PropTypes.bool,
  history: PropTypes.object.isRequired,
  getElemByAreaKey: PropTypes.func.isRequired,
  locationPathName: PropTypes.string
};

export default withAreaGetter(FacilityWizard);
